import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { Customer } from "@acto/taskpane/core/domain";
import { ClientFilter } from "@acto/gql/graphql";
import { mapClientToCustomer } from "./mapClientToCustomer";

const RankedClientsQuery = graphql(`
  query GetRankedClients($first: Int!, $after: String, $filter: ClientFilter) {
    rankedClients(first: $first, after: $after, filter: $filter) {
      __typename
      edges {
        __typename
        node {
          ...ClientForDetailsWithRecommendedObservation
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

interface FilterOptions {
  nameStartsWithOrExternalReference?: string;
}

interface Options extends FilterOptions {
  limit?: number;
  enabled?: boolean;
}

export function useRankedClients({
  limit = 5,
  nameStartsWithOrExternalReference,
  enabled = true,
}: Options = {}): PaginatedDataAccessResult<Customer> {
  const filter: ClientFilter | undefined = useMemo(
    () =>
      nameStartsWithOrExternalReference
        ? { nameStartsWithOrExternalReference: nameStartsWithOrExternalReference }
        : undefined,
    [nameStartsWithOrExternalReference],
  );

  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: RankedClientsQuery,
    limit,
    variables: { filter },
    enabled,
    connection: (data) => data.rankedClients,
  });

  const customers = useMemo(() => data?.map(mapClientToCustomer), [data]);

  return {
    data: customers,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    refreshAfter,
  };
}
